import { useMap } from "@vis.gl/react-maplibre";
import { Marker as MapLibreMarker } from "maplibre-gl";
import { useMotionValueEvent } from "motion/react";
import { FC, useEffect, useRef } from "react";

import { getAliveMap, isMapLibreReadyToPlacePins } from "@ryde/controls";

import {
  MARKER_ROTATE_SELECTOR,
  TRAIL_ACTUAL_COLOR,
  TRAIL_DETOUR_COLOR,
} from "../investigation.constants";
import { buildPlaybackMarkerHtml, sampleAt } from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";

/**
 * Playback vehicle for the investigation map. One persistent `maplibregl.Marker` is moved
 * imperatively from the playhead motion value (`setLngLat` + CSS rotation on the inner element), so the
 * vehicle animates every frame without re-rendering React.
 */
export const InvestigationPlaybackMarker: FC = () => {
  const { model, isOpen, playheadMs } = usePlaybackContext();
  const { current: mapRef } = useMap();
  const markerRef = useRef<MapLibreMarker | null>(null);
  const rotElRef = useRef<HTMLElement | null>(null);
  const lastDetourRef = useRef<boolean | null>(null);

  const active = isOpen && !!model && model.pointCount > 0;

  useEffect(() => {
    const map = getAliveMap(mapRef);
    // Imperative `addTo` has the same NaN-camera crash as a React Marker; wait until center/zoom are finite.
    if (!active || !model || !map || !isMapLibreReadyToPlacePins(map)) {
      return undefined;
    }

    const initial = sampleAt(model, playheadMs.get());
    if (!initial.latLng) {
      return undefined;
    }

    const [lat, lng] = initial.latLng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return undefined;
    }

    const element = document.createElement("div");
    element.className = "rideinv-playback-marker";
    element.innerHTML = buildPlaybackMarkerHtml(
      initial.bearingDeg,
      initial.inDetour ? TRAIL_DETOUR_COLOR : TRAIL_ACTUAL_COLOR,
    );

    const marker = new MapLibreMarker({
      element,
      // Keep the vehicle screen-aligned; map pitch is locked flat but rotation must not skew it.
      pitchAlignment: "viewport",
      rotationAlignment: "viewport",
    })
      .setLngLat([lng, lat])
      .addTo(map);

    markerRef.current = marker;
    rotElRef.current =
      element.querySelector<HTMLElement>(MARKER_ROTATE_SELECTOR) ?? null;
    lastDetourRef.current = initial.inDetour;

    return () => {
      marker.remove();
      markerRef.current = null;
      rotElRef.current = null;
      lastDetourRef.current = null;
    };
  }, [active, model, mapRef, playheadMs]);

  useMotionValueEvent(playheadMs, "change", (ms: number) => {
    const marker = markerRef.current;
    if (!marker || !model) {
      return;
    }

    const snapshot = sampleAt(model, ms);
    if (!snapshot.latLng) {
      return;
    }

    const [lat, lng] = snapshot.latLng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    marker.setLngLat([lng, lat]);

    // Re-skin only when the on/off-route state flips (rare); rotate the inner element every frame.
    if (snapshot.inDetour !== lastDetourRef.current) {
      lastDetourRef.current = snapshot.inDetour;
      const element = marker.getElement();
      element.innerHTML = buildPlaybackMarkerHtml(
        snapshot.bearingDeg,
        snapshot.inDetour ? TRAIL_DETOUR_COLOR : TRAIL_ACTUAL_COLOR,
      );
      rotElRef.current =
        element.querySelector<HTMLElement>(MARKER_ROTATE_SELECTOR) ?? null;

      return;
    }

    const rotEl =
      rotElRef.current ??
      marker.getElement().querySelector<HTMLElement>(MARKER_ROTATE_SELECTOR) ??
      null;
    rotElRef.current = rotEl;

    if (rotEl) {
      rotEl.style.transform = `rotate(${snapshot.bearingDeg}deg)`;
    }
  });

  return null;
};
